(() => {
  'use strict';

  const MAX_TIMEOUT = 2147483647;
  const MOBILE_BREAKPOINT = 750;
  const EXIT_INTENT_BREAKPOINT = 990;
  const STORAGE_FIELDS = Object.freeze({
    viewed: 'popupViewed',
    closed: 'popupClosed',
    subscribed: 'subscribed',
    lastShown: 'lastShown',
    nextEligibleAt: 'nextEligibleAt',
    pageViews: 'pageViews',
  });

  const SELECTORS = Object.freeze({
    modal: '[data-newsletter-popup-modal]',
    dialog: '[data-newsletter-popup-dialog]',
    backdrop: '[data-newsletter-popup-backdrop]',
    close: '[data-newsletter-popup-close]',
    teaser: '[data-newsletter-popup-teaser]',
    success: '[data-newsletter-popup-success]',
    error: '[data-newsletter-popup-error]',
    email: 'input[type="email"]',
    form: 'form',
    submit: 'button[type="submit"]',
  });

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  /**
   * Small localStorage wrapper. Every read/write is guarded so privacy mode,
   * blocked storage, or a full quota never breaks the storefront experience.
   */
  class SafeStorage {
    constructor(namespace) {
      this.namespace = namespace;
      this.memoryFallback = new Map();
    }

    key(field) {
      return `${this.namespace}:${field}`;
    }

    get(field, fallback = null) {
      const key = this.key(field);

      try {
        const rawValue = window.localStorage.getItem(key);
        return rawValue === null ? fallback : JSON.parse(rawValue);
      } catch (error) {
        return this.memoryFallback.has(key) ? this.memoryFallback.get(key) : fallback;
      }
    }

    set(field, value) {
      const key = this.key(field);
      this.memoryFallback.set(key, value);

      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // The in-memory value remains available for the current page session.
      }
    }

    remove(field) {
      const key = this.key(field);
      this.memoryFallback.delete(key);

      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        // Storage is optional; there is nothing else to clean up.
      }
    }

    boolean(field) {
      return this.get(field, false) === true;
    }

    number(field) {
      const value = Number(this.get(field, 0));
      return Number.isFinite(value) ? value : 0;
    }
  }

  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();

      this.modal = null;
      this.dialog = null;
      this.backdrop = null;
      this.closeButton = null;
      this.teaser = null;
      this.form = null;
      this.submitButton = null;
      this.successMessage = null;
      this.errorMessage = null;
      this.storage = null;

      this.isOpen = false;
      this.previousActiveElement = null;
      this.eligibilityTimer = null;
      this.triggerTimer = null;
      this.closeTimer = null;
      this.successTimer = null;
      this.resizeTimer = null;
      this.scrollListenerActive = false;
      this.exitListenerActive = false;
      this.lastDeviceAllowed = null;

      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleTeaserClick = this.handleTeaserClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleExitIntent = this.handleExitIntent.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.modal = this.querySelector(SELECTORS.modal);
      this.dialog = this.querySelector(SELECTORS.dialog);
      this.backdrop = this.querySelector(SELECTORS.backdrop);
      this.closeButton = this.querySelector(SELECTORS.close);
      this.teaser = this.querySelector(SELECTORS.teaser);
      this.form = this.querySelector(SELECTORS.form);
      this.submitButton = this.querySelector(SELECTORS.submit);
      this.successMessage = this.querySelector(SELECTORS.success);
      this.errorMessage = this.querySelector(SELECTORS.error);
      this.storage = new SafeStorage(this.dataset.storageKey || `newsletter-popup:${this.dataset.sectionId || 'global'}`);

      if (!this.modal || !this.dialog || !this.closeButton || !this.teaser) return;

      this.bindEvents();
      this.trackPageView();
      this.lastDeviceAllowed = this.isDeviceAllowed();

      if (this.isDesignMode()) {
        window.requestAnimationFrame(() => {
          this.open({ source: 'theme-editor', force: true, persistView: false });
        });
        return;
      }

      if (this.successMessage) {
        this.handleSuccessfulSubmission();
        return;
      }

      if (this.errorMessage) {
        this.open({ source: 'form-error', force: true });
        return;
      }

      if (this.storage.boolean(STORAGE_FIELDS.closed)) {
        this.showTeaser();
      }

      this.scheduleAutomaticOpen();
    }

    disconnectedCallback() {
      if (this.dataset.initialized !== 'true') return;

      this.clearAllTimers();
      this.removeTriggerListeners();
      this.unbindEvents();
      this.releasePageLock();
      this.dataset.initialized = 'false';
    }

    bindEvents() {
      this.closeButton.addEventListener('click', this.handleCloseClick);
      this.backdrop?.addEventListener('click', this.handleBackdropClick);
      this.teaser.addEventListener('click', this.handleTeaserClick);
      this.form?.addEventListener('submit', this.handleFormSubmit);
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    unbindEvents() {
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.backdrop?.removeEventListener('click', this.handleBackdropClick);
      this.teaser?.removeEventListener('click', this.handleTeaserClick);
      this.form?.removeEventListener('submit', this.handleFormSubmit);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    isEnabled() {
      return this.dataset.enabled === 'true';
    }

    isRepeatEnabled() {
      return this.dataset.repeatEnabled === 'true';
    }

    isDeviceAllowed() {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      return isMobile ? this.dataset.showMobile === 'true' : this.dataset.showDesktop === 'true';
    }

    isExitIntentDevice() {
      return window.matchMedia(
        `(min-width: ${EXIT_INTENT_BREAKPOINT}px) and (hover: hover) and (pointer: fine)`
      ).matches;
    }

    getFrequencyMilliseconds() {
      const amount = Math.max(1, Number.parseInt(this.dataset.frequencyNumber || '10', 10));
      const unit = this.dataset.frequencyType || 'minutes';
      const multipliers = {
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
      };

      return amount * (multipliers[unit] || multipliers.minutes);
    }

    getMillisecondsUntilEligible() {
      if (this.storage.boolean(STORAGE_FIELDS.subscribed)) return Number.POSITIVE_INFINITY;

      if (!this.isRepeatEnabled()) {
        const hasAlreadyDisplayed =
          this.storage.boolean(STORAGE_FIELDS.viewed) || this.storage.boolean(STORAGE_FIELDS.closed);
        return hasAlreadyDisplayed ? Number.POSITIVE_INFINITY : 0;
      }

      const nextEligibleAt = this.storage.number(STORAGE_FIELDS.nextEligibleAt);
      return Math.max(0, nextEligibleAt - Date.now());
    }

    trackPageView() {
      if (this.isDesignMode()) return;

      window.__newsletterPopupCountedSections = window.__newsletterPopupCountedSections || new Set();
      const countKey = this.dataset.storageKey || this.dataset.sectionId || 'global';

      if (window.__newsletterPopupCountedSections.has(countKey)) return;
      window.__newsletterPopupCountedSections.add(countKey);

      const nextCount = this.storage.number(STORAGE_FIELDS.pageViews) + 1;
      this.storage.set(STORAGE_FIELDS.pageViews, nextCount);
    }

    scheduleAutomaticOpen() {
      this.clearAutomaticTimers();
      this.removeTriggerListeners();

      if (this.isDesignMode() || !this.isEnabled() || !this.isDeviceAllowed()) return;
      if (this.storage.boolean(STORAGE_FIELDS.subscribed)) {
        this.hideTeaser();
        return;
      }

      const waitTime = this.getMillisecondsUntilEligible();
      if (!Number.isFinite(waitTime)) return;

      if (waitTime > 0) {
        this.eligibilityTimer = window.setTimeout(
          () => this.scheduleAutomaticOpen(),
          Math.min(waitTime, MAX_TIMEOUT)
        );
        return;
      }

      this.armConfiguredTrigger();
    }

    armConfiguredTrigger() {
      const trigger = this.dataset.trigger || 'timer';

      switch (trigger) {
        case 'immediately':
          this.triggerTimer = window.setTimeout(() => this.open({ source: 'immediately' }), 80);
          break;

        case 'timer': {
          const seconds = Math.min(120, Math.max(1, Number.parseInt(this.dataset.timerSeconds || '40', 10)));
          this.triggerTimer = window.setTimeout(() => this.open({ source: 'timer' }), seconds * 1000);
          break;
        }

        case 'scroll':
          this.scrollListenerActive = true;
          window.addEventListener('scroll', this.handleScroll, { passive: true });
          this.handleScroll();
          break;

        case 'exit_intent':
          if (this.dataset.exitIntentEnabled === 'true' && this.isExitIntentDevice()) {
            this.exitListenerActive = true;
            document.addEventListener('mouseout', this.handleExitIntent);
          }
          break;

        case 'page_views': {
          const requiredViews = Math.min(10, Math.max(1, Number.parseInt(this.dataset.pageViews || '1', 10)));
          if (this.storage.number(STORAGE_FIELDS.pageViews) >= requiredViews) {
            this.triggerTimer = window.setTimeout(() => this.open({ source: 'page-views' }), 80);
          }
          break;
        }

        default:
          this.triggerTimer = window.setTimeout(() => this.open({ source: 'default' }), 80);
      }
    }

    handleScroll() {
      const documentElement = document.documentElement;
      const scrollableDistance = Math.max(1, documentElement.scrollHeight - window.innerHeight);
      const progress = Math.min(100, Math.max(0, (window.scrollY / scrollableDistance) * 100));
      const requiredProgress = Math.min(100, Math.max(10, Number.parseInt(this.dataset.scrollPercent || '50', 10)));

      if (progress >= requiredProgress) {
        this.open({ source: 'scroll' });
      }
    }

    handleExitIntent(event) {
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > 8) return;
      this.open({ source: 'exit-intent' });
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        const deviceAllowed = this.isDeviceAllowed();

        if (this.isOpen && !deviceAllowed && !this.isDesignMode()) {
          this.close({ reason: 'device-change', persistClosed: false, displayTeaser: false });
        }

        if (deviceAllowed !== this.lastDeviceAllowed) {
          this.lastDeviceAllowed = deviceAllowed;
          this.hideTeaser();

          if (deviceAllowed) {
            if (this.storage.boolean(STORAGE_FIELDS.closed)) this.showTeaser();
            if (!this.isOpen) this.scheduleAutomaticOpen();
          } else {
            this.clearAutomaticTimers();
            this.removeTriggerListeners();
          }
        }
      }, 150);
    }

    handleFormSubmit(event) {
      const form = event.currentTarget;
      if (!form.checkValidity()) return;

      form.setAttribute('aria-busy', 'true');
      if (this.submitButton) this.submitButton.disabled = true;
    }

    handleSuccessfulSubmission() {
      this.storage.set(STORAGE_FIELDS.subscribed, true);
      this.storage.set(STORAGE_FIELDS.closed, true);
      this.clearAutomaticTimers();
      this.removeTriggerListeners();
      this.hideTeaser();

      this.open({ source: 'success', force: true });

      this.successTimer = window.setTimeout(() => {
        this.close({ reason: 'success', persistClosed: false, displayTeaser: false });
      }, 3000);
    }

    canOpenAutomatically() {
      if (!this.isEnabled() || !this.isDeviceAllowed()) return false;
      if (this.storage.boolean(STORAGE_FIELDS.subscribed)) return false;
      return this.getMillisecondsUntilEligible() === 0;
    }

    open({ source = 'manual', force = false, persistView = true } = {}) {
      if (this.isOpen) return;
      if (this.storage.boolean(STORAGE_FIELDS.subscribed) && source !== 'success' && !this.isDesignMode()) return;
      if (!force && !this.canOpenAutomatically()) return;
      if (!force && !this.isDeviceAllowed()) return;

      window.clearTimeout(this.closeTimer);
      this.clearAutomaticTimers();
      this.removeTriggerListeners();
      this.hideTeaser();

      this.previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.isOpen = true;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.remove('is-closing');

      this.applyPageLock();
      document.addEventListener('keydown', this.handleDocumentKeydown);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
          this.focusInitialElement();
        });
      });

      if (!this.isDesignMode() && persistView) {
        const now = Date.now();
        this.storage.set(STORAGE_FIELDS.viewed, true);
        this.storage.set(STORAGE_FIELDS.lastShown, now);

        if (this.isRepeatEnabled()) {
          this.storage.set(STORAGE_FIELDS.nextEligibleAt, now + this.getFrequencyMilliseconds());
        }
      }

      this.dispatchEvent(
        new CustomEvent('newsletter-popup:opened', {
          bubbles: true,
          detail: { sectionId: this.dataset.sectionId, source },
        })
      );
    }

    close({ reason = 'user', persistClosed = true, displayTeaser = true } = {}) {
      if (!this.isOpen) return;

      this.isOpen = false;
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      this.modal.classList.remove('is-open');
      this.modal.classList.add('is-closing');

      if (!this.isDesignMode() && persistClosed) {
        this.storage.set(STORAGE_FIELDS.closed, true);
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const closeDelay = prefersReducedMotion ? 0 : 330;

      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.modal.hidden = true;
        this.modal.setAttribute('aria-hidden', 'true');
        this.modal.classList.remove('is-closing');
        this.releasePageLock();

        if (displayTeaser) this.showTeaser();
        this.restoreFocus();

        if (this.isRepeatEnabled() && reason !== 'success' && reason !== 'theme-editor') {
          this.scheduleAutomaticOpen();
        }

        this.dispatchEvent(
          new CustomEvent('newsletter-popup:closed', {
            bubbles: true,
            detail: { sectionId: this.dataset.sectionId, reason },
          })
        );
      }, closeDelay);
    }

    handleCloseClick() {
      this.close({ reason: 'close-button' });
    }

    handleBackdropClick(event) {
      if (event.currentTarget !== event.target) return;
      this.close({ reason: 'backdrop' });
    }

    handleTeaserClick() {
      this.open({ source: 'teaser', force: true });
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ reason: 'escape' });
        return;
      }

      if (event.key !== 'Tab') return;
      this.trapFocus(event);
    }

    trapFocus(event) {
      const focusableElements = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || activeElement === this.dialog)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    focusInitialElement() {
      const preferredElement = this.successMessage || this.querySelector(SELECTORS.email) || this.closeButton;
      const target = preferredElement || this.dialog;

      window.setTimeout(() => {
        if (!this.isOpen || !target) return;
        target.focus({ preventScroll: true });
      }, 40);
    }

    restoreFocus() {
      const target = this.previousActiveElement;
      this.previousActiveElement = null;

      if (target && target.isConnected && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }

    showTeaser() {
      if (this.dataset.showTeaser !== 'true') return;
      if (!this.isDeviceAllowed() && !this.isDesignMode()) return;
      if (this.storage.boolean(STORAGE_FIELDS.subscribed)) return;
      if (this.isOpen) return;

      this.teaser.hidden = false;
    }

    hideTeaser() {
      if (this.teaser) this.teaser.hidden = true;
    }

    applyPageLock() {
      document.documentElement.classList.add('newsletter-popup-is-open');
      document.body.classList.add('newsletter-popup-is-open');
    }

    releasePageLock() {
      const anotherPopupIsOpen = Array.from(document.querySelectorAll(SELECTORS.modal)).some(
        (modal) => modal !== this.modal && modal.classList.contains('is-open')
      );

      if (anotherPopupIsOpen) return;
      document.documentElement.classList.remove('newsletter-popup-is-open');
      document.body.classList.remove('newsletter-popup-is-open');
    }

    handleSectionSelect(event) {
      const selectedSectionId = event.detail?.sectionId;
      const selectedThisSection = selectedSectionId
        ? selectedSectionId === this.dataset.sectionId
        : event.target?.contains?.(this);

      if (selectedThisSection) {
        this.open({ source: 'theme-editor', force: true, persistView: false });
      }
    }

    handleSectionDeselect(event) {
      const deselectedSectionId = event.detail?.sectionId;
      const deselectedThisSection = deselectedSectionId
        ? deselectedSectionId === this.dataset.sectionId
        : event.target?.contains?.(this);

      if (deselectedThisSection) {
        this.close({ reason: 'theme-editor', persistClosed: false, displayTeaser: false });
      }
    }

    removeTriggerListeners() {
      if (this.scrollListenerActive) {
        window.removeEventListener('scroll', this.handleScroll);
        this.scrollListenerActive = false;
      }

      if (this.exitListenerActive) {
        document.removeEventListener('mouseout', this.handleExitIntent);
        this.exitListenerActive = false;
      }
    }

    clearAutomaticTimers() {
      window.clearTimeout(this.eligibilityTimer);
      window.clearTimeout(this.triggerTimer);
      this.eligibilityTimer = null;
      this.triggerTimer = null;
    }

    clearAllTimers() {
      this.clearAutomaticTimers();
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.successTimer);
      window.clearTimeout(this.resizeTimer);
      this.closeTimer = null;
      this.successTimer = null;
      this.resizeTimer = null;
    }
  }

  if (!customElements.get('newsletter-popup')) {
    customElements.define('newsletter-popup', NewsletterPopup);
  }
})();
